import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { MapPin } from 'lucide-react';
import { getPreferredTileStyle } from '../../constants/mapTiles';
import 'leaflet/dist/leaflet.css';
import './CoordinatePicker.css';

// Import marker images
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix for default marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface CoordinatePickerProps {
  latitude: number;
  longitude: number;
  onCoordinateChange: (lat: number, lng: number) => void;
  height?: string;
}

function LocationMarker({ position, onPositionChange }: {
  position: [number, number];
  onPositionChange: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      onPositionChange(e.latlng.lat, e.latlng.lng);
    },
  });

  return position ? <Marker position={position} /> : null;
}

export function CoordinatePicker({
  latitude,
  longitude,
  onCoordinateChange,
  height = '400px'
}: CoordinatePickerProps) {
  const [position, setPosition] = useState<[number, number]>([latitude || 52.52, longitude || 13.405]);

  useEffect(() => {
    if (latitude && longitude) {
      setPosition([latitude, longitude]);
    }
  }, [latitude, longitude]);

  const handlePositionChange = (lat: number, lng: number) => {
    setPosition([lat, lng]);
    onCoordinateChange(lat, lng);
  };

  return (
    <div className="coordinate-picker" style={{ height }}>
      <MapContainer
        center={position}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        key={`${position[0]}-${position[1]}`}
      >
        <TileLayer
          attribution={getPreferredTileStyle().attribution}
          url={getPreferredTileStyle().url}
          errorTileUrl="/tiles/fallback.png"
        />
        <LocationMarker position={position} onPositionChange={handlePositionChange} />
      </MapContainer>
      <div className="coordinate-display">
        <span><MapPin size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />Lat: {position[0].toFixed(6)}, Lng: {position[1].toFixed(6)}</span>
        <span className="click-hint">Click on map to set coordinates</span>
      </div>
    </div>
  );
}
